/**
 * Home-page hero — standalone frontend entry.
 *
 * Upgrades the `[data-home-hero]` section into the live, keyed WebGL gallery
 * (HomeGalleryEngine), reproduced from the page's gallery images plus a colour
 * palette picked at random per page load from HERO_PALETTES (below). Built on the
 * integrated image styler and mirrors the frontend-view pattern of
 * src/image-styler-view.js / src/page-bg-view.js (reduced-motion bail, reveal
 * crossfade, window pointer, run/stop clock).
 *
 * Swiper is the CONTROLLER only — slide index, loop, autoplay cadence, the
 * autoplay timer ring, and pause/resume. The WebGL canvas does all the visuals
 * and owns the 3-texture slide rotation; Swiper's own DOM slides sit BEHIND the
 * canvas purely as the no-JS / no-WebGL fallback. The scroll gesture is reserved
 * for the scroll-timeline (param ramp → colour takeover → fade-to-black + reveal).
 *
 * Its own top-level Vite entry (ES module), enqueued only on the front page. It
 * shares the `swiper.js` chunk with `main` (slider.js) — `main` is therefore
 * enqueued as a module too (see library/enqueue-scripts.php).
 */

import Swiper from 'swiper'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import { HomeGalleryEngine, T_END, FADE_TO_LIGHT, FADE_TO_DARK } from './home/engine.js'
import './sass/home.sass'

// Scroll timeline (engine ramps over 0..T_MID; takeover/fade past it).
const T_MID = 1.0
const T_PRAEND = 1.8
// The reveal (calendar) starts rising up from below the fold here — kept past
// LOCK_AFTER so it enters only once the takeover has committed and the title /
// image are clearing, then slides + fades up to T_END. Lower = appears sooner.
const T_REVEAL = 1.3
// The whole scroll-timeline (0 → T_END) plays out over this many viewport heights
// of scroll input. Deriving the step from the LIVE viewport height (not a fixed px
// sensitivity) makes the intro the same length on every screen. Lower = the
// calendar is reached with less scrolling. Touch keeps the designer's ~2× quicker
// feel (TOUCH_SPEEDUP), so it reaches the calendar in ~half this.
const SCROLL_LIMIT_VH = 2
// Phones get a shorter intro: a swipe has no inertia here (the timeline
// preventDefault()s the gesture), so every pixel must be dragged by the finger.
// 1.2vh × TOUCH_SPEEDUP ≈ 0.6 viewport heights of travel — about one comfortable
// swipe, instead of the two-plus full-screen drags the desktop length demanded.
const SCROLL_LIMIT_VH_MOBILE = 1.2
const MOBILE_MAX_WIDTH = 767 // matches the SASS `medium` breakpoint (768px)
const TOUCH_SPEEDUP = 2
// Normalise wheel deltas to pixels first: Firefox reports deltaY in lines
// (deltaMode 1) and whole pages (deltaMode 2), so without this a line-mode wheel
// needed dozens of extra notches to play the timeline — the "scroll goes too long".
const WHEEL_LINE_PX = 16

// A touch advances the gallery only on a "true tap": finger travel and dwell
// under these read as a tap; beyond them the touch is a scroll gesture (which
// drives the scroll-timeline) and never changes a slide.
const TAP_MOVE_MAX = 10  // px of finger travel still counted as a tap
const TAP_TIME_MAX = 500 // ms; longer reads as a hold/scroll, not a tap

// Lock autoplay + click-advance once scrolled into the takeover (with hysteresis).
const LOCK_AFTER = 1.25
const LOCK_HYSTERESIS = 0.06

const AUTOPLAY_MS = 5000
// Ignore advance triggers closer than this (loop clone-fix re-fires, init, click spam).
const MIN_ADVANCE_MS = 600

// Slide-change speed for the DOM Swiper. Under `.reduce-motion` it MUST be 0:
// 90_animations.sass collapses every transition to `transition: none !important`
// there, and Swiper only clears its internal `animating` flag from a
// `transitionend` on the wrapper — which then never fires. In loop mode
// `slideNext()` bails on `animating && loopPreventsSliding`, so the gallery
// advanced exactly once and then froze forever. speed 0 takes Swiper's
// synchronous transitionEnd path instead, so nothing waits on a CSS transition
// (and an instant slide change is what reduce-motion wants anyway — the WebGL
// canvas owns the visible morph).
const SWIPER_SPEED = 400
const SWIPER_SPEED_REDUCED = 0

// Below this mean luma a slide reads as "dark" → the (black) header logo flips to
// white so it stays legible over the slideshow. The hero shows ~the raw photo at
// rest, so the engine's per-image luminance faithfully proxies the logo backdrop.
const LOGO_DARK_CUT = 0.5

// Scroll point past which the logo stops tracking the slide and follows the site
// COLOUR MODE instead. Once the timeline starts, the colour takeover and (past
// T_MID) the flood wash the hero to the page background and the calendar reveal
// rises underneath, so the slide's brightness no longer describes what's behind the
// logo: the end state is white in light mode (→ black logo) and black in dark mode
// (→ white logo). Below this the logo still tracks the live slide brightness (and
// scrolling back up restores it).
const LOGO_MODE_AFTER = 0.25

// Cap the live GPU draw rate. Match the designer prototype's ~60fps: both the
// mouse sample (setMouse) and the draw sit behind this gate, so 60fps also
// restores responsive mouse-keying — the fluid motion the OG had.
const FRAME_MS = 1000 / 60

// Cap the drawing-buffer resolution below the engine's 2× DPR. The hero is
// full-screen, so 1.5× roughly halves fragment cost on retina with only a slight
// softening. Weak devices (≤4 GB RAM or ≤4 cores) start at 1.0× to cut fragment
// cost / heat further — sharper devices keep 1.5×.
const LIVE_MAX_DPR =
  (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
  (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ? 1.0 : 1.5

// Five hand-picked "Bildstil" palettes (the designer's palette_01..05 prototypes,
// except the last: the client dropped Brown #D89638 in favour of the Identity
// Orange #FF8121 from library/data/bg-styles.json).
// The hero no longer pulls a single preset from WordPress — instead one of these
// is chosen at random on every page load. Each is just the keyed colour set
// (c0..c4) the WebGL shader consumes via engine.setPalette(); all other params
// come from the engine's BASE/SCROLLED ramp.
const HERO_PALETTES = [
  { c0: '#646564', c1: '#d8cbce', c2: '#E100E4', c3: '#ff6be4', c4: '#eeeeee' },
  { c0: '#00ccb3', c1: '#d1cfda', c2: '#00BA3E', c3: '#ff6be4', c4: '#eeeeee' },
  { c0: '#00ccb3', c1: '#cac7b3', c2: '#006de9', c3: '#ff6be4', c4: '#eeeeee' },
  { c0: '#00ccb3', c1: '#f6f6f6', c2: '#fffdc0', c3: '#ff6be4', c4: '#eeeeee' },
  { c0: '#00ccb3', c1: '#cac7b3', c2: '#FF8121', c3: '#ff6be4', c4: '#eeeeee' },
]
const pickHeroPalette = () => HERO_PALETTES[Math.floor(Math.random() * HERO_PALETTES.length)]

// Metered / data-saver gate: the CHEAPEST path (flat static Swiper, no WebGL at
// all). Weak/metered sessions keep the plain fallback.
const isReducedData = () =>
  window.matchMedia('(prefers-reduced-data: reduce)').matches ||
  (navigator.connection && navigator.connection.saveData === true)

// Reduced-motion gate: the visitor's Quiet-Mode toggle / OS reduce-motion. The
// body class (set pre-paint in header.php, honouring the Quiet-Mode opt-out) is
// authoritative; the media query is a fallback. These sessions get the SAME
// layered gallery as fancy mode but WITHOUT the keyed shader and paused by default
// (initReducedMotionGallery) — not the flat slideshow.
const prefersReducedMotion = () =>
  document.body.classList.contains('reduce-motion') ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const clamp = (x, a, b) => Math.max(a, Math.min(b, x))
const clamp01 = (x) => clamp(x, 0, 1)
const smoothstep = (e0, e1, x) => { const t = clamp01((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t) }

// px of scroll input that plays the whole timeline (0 → T_END): SCROLL_LIMIT_VH
// viewport heights, read LIVE so it tracks resizes / rotation / mobile toolbar changes
// (the width test rides along, so rotating a phone re-picks the right length).
const scrollLimitVh = () =>
  window.innerWidth <= MOBILE_MAX_WIDTH ? SCROLL_LIMIT_VH_MOBILE : SCROLL_LIMIT_VH
const scrollSpan = () => Math.max(1, window.innerHeight * scrollLimitVh())
// Timeline advance per pixel of (pixel-normalised) scroll input.
const scrollStepPerPx = () => T_END / scrollSpan()
// Wheel deltaY → pixels, honouring Firefox's line (1) / page (2) delta modes.
const wheelDeltaPx = (e) =>
  e.deltaMode === 1 ? e.deltaY * WHEEL_LINE_PX
    : e.deltaMode === 2 ? e.deltaY * window.innerHeight
      : e.deltaY

function init(hero) {
  // 1. Image sources. Bail (keep the fallback images) if unusable.
  const sources = [...hero.querySelectorAll('[data-home-src]')].map((n) => n.dataset.homeSrc).filter(Boolean)
  if (!sources.length) return
  // The reveal (calendar + footer) is a normal-flow block below the hero in both
  // reduced paths, so clear its `inert` NOW (the fancy path only clears it once the
  // timeline locks — line ~185) or the calendar links / footer form stay dead.
  // Also assert the reduce-motion layout class so that flow layout applies even in
  // the media-only / Save-Data edge where header.php didn't set it (else the reveal
  // stays in its fancy fixed-hidden base state and the calendar is unreachable).
  if (isReducedData()) {
    // Metered / data-saver: cheapest path — flat static slideshow, no WebGL.
    document.body.classList.add('reduce-motion')
    const reveal = document.querySelector('.home-hero__reveal')
    if (reveal) reveal.inert = false
    initReducedMotionSlideshow(hero)
    // Metered, not motion-averse — a smooth scroll costs no data.
    wireScrollDown(hero, () => scrollDocumentToReveal('smooth'))
    return
  }
  if (prefersReducedMotion()) {
    // Reduced-motion: the fancy layered gallery WITHOUT the keyed shader, paused by
    // default (nothing moves until the visitor taps / presses play). Normal flow —
    // no fixed takeover, no scroll-timeline.
    document.body.classList.add('reduce-motion')
    const reveal = document.querySelector('.home-hero__reveal')
    if (reveal) reveal.inert = false
    initReducedMotionGallery(hero)
    return
  }

  // 2. WebGL engine — bail (keep fallback) if unavailable.
  const canvas = document.createElement('canvas')
  canvas.className = 'home-hero__canvas'
  canvas.setAttribute('aria-hidden', 'true')
  hero.appendChild(canvas)

  let engine
  try { engine = new HomeGalleryEngine(canvas) }
  catch { canvas.remove(); return }
  engine.maxDpr = LIVE_MAX_DPR
  // Cache + upload hero images at ≤1280² (vs the 2048² GL cap): ~4× less RAM for the
  // per-image square canvases AND smaller GPU textures. Only the hero needs this.
  engine.maxSquare = Math.min(1280, engine.maxSquare)

  engine.setPalette(pickHeroPalette()) // random palette per load (no longer from WordPress)
  document.body.classList.add('home-hero-active') // locks page scroll (fallback keeps normal flow)

  // 3. Run state.
  const scrollEnabled = hero.hasAttribute('data-home-scroll') // 404 omits it → static slider, no scroll-timeline
  const reveal = document.querySelector('.home-hero__reveal') // sibling of the hero now; inert until locked → calendar only reachable when revealed
  const mouse = { x: 0.5, y: 0.5 }
  let scrollT = 0      // target (driven by wheel/touch)
  let scrollT_s = 0    // smoothed value fed to the engine + CSS
  let locked = false
  let userPaused = false
  let running = false
  let revealed = false
  let rafId = 0
  let t0 = 0
  let elapsed = 0
  let swiper = null
  let lastAdvance = 0
  let lastDraw = 0     // performance.now() of the last GPU draw (fps cap)
  let hidden = false   // tab visibility (visibilitychange)

  const revealIfReady = () => {
    if (revealed || canvas.width <= 0 || canvas.height <= 0) return
    revealed = true
    hero.classList.add('is-home-live') // canvas ready → CSS crossfades the fallback out
  }

  // Dark/light state of the CURRENT slide behind the logo (last measured). The logo
  // only actually inverts while the hero still shows that photo — once scrolling
  // down, applyLogoColor hands over to the colour mode.
  let logoSlideDark = false

  const isDarkMode = () => document.body.classList.contains('dark-mode')

  // Apply the logo colour: over the live slideshow it inverts to white on a dark
  // slide; past LOGO_MODE_AFTER the hero has washed to the page background, so it
  // follows the colour mode instead (white logo in dark mode — otherwise a black
  // logo would vanish into the black end flood). Idempotent — toggle is a no-op when
  // unchanged, so it's cheap to call every frame.
  const applyLogoColor = () => {
    const overSlide = scrollT_s < LOGO_MODE_AFTER
    document.body.classList.toggle('logo-on-dark', overSlide ? logoSlideDark : isDarkMode())
  }

  // Publish the brightness of the slide behind the logo so the (black) header
  // logo can invert to white over dark images. `body.logo-on-dark` drives the
  // CSS (30_header.sass); the window event lets anything else react too.
  const publishLogoBrightness = (mean) => {
    if (mean == null) return
    const isDark = mean < LOGO_DARK_CUT
    logoSlideDark = isDark
    applyLogoColor()
    window.dispatchEvent(new CustomEvent('home-hero:brightness', { detail: { mean, isDark } }))
  }

  const writeScrollVars = () => {
    const cssT = clamp01((scrollT_s - T_MID) / (T_PRAEND - T_MID))
    const cssB = clamp01((scrollT_s - T_REVEAL) / (T_END - T_REVEAL))
    const root = document.documentElement.style
    root.setProperty('--scroll-fade', (1 - smoothstep(0, 1, cssT)).toFixed(4))     // title etc. fade out
    root.setProperty('--scroll-fade-up', smoothstep(0, 1, cssB).toFixed(4))        // reveal slides up + fades in
  }

  const syncAutoplay = () => {
    if (!swiper || !swiper.autoplay) return
    const shouldRun = !locked && !userPaused
    if (shouldRun && !swiper.autoplay.running) swiper.autoplay.start()
    else if (!shouldRun && swiper.autoplay.running) swiper.autoplay.stop()
  }

  // Once the scroll-timeline is fully played out the canvas has faded (whiteFade=1)
  // and the reveal layer covers it — nothing animates, so park the loop there. Both
  // the target AND the smoothed value must be at the end, so a pending scroll-back
  // keeps it running. The static 404 variant has no timeline, so it never parks.
  const timelineParked = () =>
    scrollEnabled && scrollT >= T_END - 1e-3 && scrollT_s >= T_END - 1e-3

  const tick = (now) => {
    if (engine._lost) { onLost(); return } // GPU context lost → static fallback
    if (hidden || timelineParked()) { stop(); return } // nothing to animate → pause
    rafId = requestAnimationFrame(tick)
    // Cheap timeline/lock math runs every frame so scrolling stays responsive…
    if (scrollEnabled) {
      scrollT_s += (scrollT - scrollT_s) * 0.12 // smooth (prototype feel)
      engine.setScroll(scrollT_s)
      writeScrollVars()
      applyLogoColor() // scroll may have crossed LOGO_MODE_AFTER → slide brightness vs colour mode
      const nextLocked = locked ? scrollT_s > LOCK_AFTER - LOCK_HYSTERESIS : scrollT_s >= LOCK_AFTER
      if (nextLocked !== locked) { locked = nextLocked; hero.classList.toggle('is-locked', locked); if (reveal) reveal.inert = !locked; syncAutoplay() }
    }
    // …but the expensive GPU draw is capped to FRAME_MS.
    if (now - lastDraw < FRAME_MS) return
    lastDraw = now
    engine.setMouse(mouse.x, mouse.y)
    engine.render(elapsed + (now - t0) / 1000)
    revealIfReady()
  }
  const start = () => { if (running) return; running = true; t0 = performance.now(); rafId = requestAnimationFrame(tick) }
  const stop = () => { if (!running) return; running = false; cancelAnimationFrame(rafId); elapsed += (performance.now() - t0) / 1000 }
  // Resume after a park / tab-hidden pause once there's something to animate again.
  const wake = () => { if (!hidden && !timelineParked() && !engine._lost) start() }
  // GPU context lost → stop the loop and degrade to the static Swiper fallback in
  // normal page flow (un-fix the takeover, free the wheel gate).
  const onLost = () => {
    stop()
    document.body.classList.remove('home-hero-active', 'logo-on-dark')
    hero.classList.remove('is-home-live')
    canvas.remove()
    scrollT = T_END
  }

  // Colour mode → what the scroll end-state floods to: white in light mode, BLACK in
  // dark mode, so the hero lands on the page background (body.dark-mode swaps
  // --background-color to black). The a11y overlay flips that class live — no reload,
  // unlike quiet mode — so watch it instead of reading it once. Guarded on an actual
  // dark/light change, since applyLogoColor's own body-class toggle re-enters here.
  let fadeDark = null
  const applyColorMode = () => {
    const dark = isDarkMode()
    if (dark === fadeDark) return
    fadeDark = dark
    engine.setFadeTarget(dark ? FADE_TO_DARK : FADE_TO_LIGHT)
    applyLogoColor() // past LOGO_MODE_AFTER the logo follows the mode → flip it too
    // Parked at the end of the timeline the render loop is stopped, so nothing would
    // pick the new target up — redraw that one settled frame by hand (only once the
    // canvas has actually rendered, i.e. `revealed`).
    if (!running && revealed) engine.render(elapsed)
  }
  applyColorMode()
  new MutationObserver(applyColorMode).observe(document.body, { attributes: true, attributeFilter: ['class'] })

  // 4. Pointer (window-level: the hero is fixed/full-screen). Cache the canvas rect
  // so each move doesn't force a layout with getBoundingClientRect — the hero is
  // fixed full-screen, so the rect only changes on resize.
  let canvasRect = canvas.getBoundingClientRect()
  window.addEventListener('resize', () => { canvasRect = canvas.getBoundingClientRect() }, { passive: true })
  window.addEventListener('pointermove', (e) => {
    const r = canvasRect
    if (r.width > 0 && r.height > 0) {
      mouse.x = (e.clientX - r.left) / r.width
      mouse.y = 1 - (e.clientY - r.top) / r.height
    }
  })

  // Scroll-chaining: once the calendar is fully up it's a real scroll container,
  // so a gesture it can still consume in its own direction scrolls the CALENDAR
  // and the timeline holds; only at the calendar's scroll boundary does the
  // timeline take back over (scroll up past its top → slide it back down, hero
  // returns). Without this the calendar's own scroll traps the gesture and you
  // can't scroll back out. dy > 0 = scrolling down, dy < 0 = up.
  const revealConsumes = (dy) => {
    if (!reveal || scrollT < T_END - 0.01) return false // only once the calendar is fully revealed
    if (dy > 0) return reveal.scrollTop + reveal.clientHeight < reveal.scrollHeight - 1 // room below → scroll down
    if (dy < 0) return reveal.scrollTop > 1                                              // room above → scroll up
    return false
  }

  // An overlay (search, calendar, …) open on top of the hero owns its own
  // scroll. Overlays are position:fixed full-viewport and drop their `inert`
  // attribute only while open, so any wheel/touch during that time targets one.
  // Bail out then — otherwise the preventDefault below eats the gesture and the
  // overlay's inner content can't scroll.
  const overlayOwnsGesture = (e) =>
    e.target instanceof Element && !!e.target.closest('.overlay:not([inert])')

  // Click on the "↓" → play the virtual timeline through to the end (the calendar),
  // since there is no document scroll to animate here. Ramped on `scrollT` — the
  // TARGET — so the existing 0.12 smoothing toward `scrollT_s` still shapes the
  // motion exactly as a real gesture would; a hard `scrollT = T_END` would ease out
  // over a full second from a jump-start and read as a snap. Any real wheel/touch
  // input cancels the ramp so the visitor keeps control mid-flight.
  const SCROLLDOWN_MS = 1400
  let scrollAnim = 0
  const cancelScrollAnim = () => { if (scrollAnim) { cancelAnimationFrame(scrollAnim); scrollAnim = 0 } }
  const animateScrollTo = (target) => {
    cancelScrollAnim()
    const from = scrollT
    const startedAt = performance.now()
    const step = (now) => {
      const p = clamp01((now - startedAt) / SCROLLDOWN_MS)
      scrollT = from + (target - from) * smoothstep(0, 1, p)
      wake() // the timeline may be parked at its end → resume the loop
      scrollAnim = p < 1 ? requestAnimationFrame(step) : 0
    }
    scrollAnim = requestAnimationFrame(step)
  }

  // 5. Scroll gesture → virtual timeline (does not scroll the document).
  // Skipped on the static variant (404), which has nothing to scroll to.
  if (scrollEnabled) {
    window.addEventListener('wheel', (e) => {
      if (overlayOwnsGesture(e)) return // let the open overlay scroll natively
      if (revealConsumes(wheelDeltaPx(e))) return // let the calendar scroll itself; timeline holds
      cancelScrollAnim() // real input wins over a running "↓" ramp
      // The timeline is fully reversible: scrolling up at the calendar's top edge
      // rewinds it back into the hero (revealConsumes above hands the gesture over
      // only at that boundary). It used to latch at T_END once reached — the client
      // asked for that to go, so don't reintroduce a floor here.
      scrollT = clamp(scrollT + wheelDeltaPx(e) * scrollStepPerPx(), 0, T_END)
      wake() // input may move the timeline off its parked end → resume the loop
      e.preventDefault() // timeline owns this gesture → never scroll the document
    }, { passive: false })
    let lastTouchY = null
    window.addEventListener('touchstart', (e) => { lastTouchY = e.touches[0].clientY }, { passive: true })
    window.addEventListener('touchmove', (e) => {
      if (overlayOwnsGesture(e)) return // let the open overlay scroll natively
      if (e.touches.length !== 1) { lastTouchY = null; return } // leave multi-touch (pinch-zoom) to the browser
      const y = e.touches[0].clientY
      const dy = (lastTouchY ?? y) - y // finger up = positive = scroll down (matches wheel deltaY sign)
      lastTouchY = y
      if (revealConsumes(dy)) return // let the calendar scroll itself; timeline holds
      cancelScrollAnim() // real input wins over a running "↓" ramp
      scrollT = clamp(scrollT + dy * scrollStepPerPx() * TOUCH_SPEEDUP, 0, T_END)
      wake()
      e.preventDefault() // timeline owns this gesture → suppress native scroll/bounce
    }, { passive: false })
  }

  wireScrollDown(hero, () => animateScrollTo(T_END))

  document.addEventListener('visibilitychange', () => {
    hidden = document.hidden
    if (hidden) stop(); else wake()
  })

  // 6. Load images, then start the loop + Swiper controller.
  engine.setImages(sources).then(() => {
    lastAdvance = performance.now() // suppress any realIndexChange emitted during init
    publishLogoBrightness(engine.logoBackdropMean()) // seed the logo colour for the first slide
    swiper = buildSwiper(hero, {
      onAdvance: () => {
        if (locked) return
        const now = performance.now()
        if (now - lastAdvance < MIN_ADVANCE_MS) return // ignore loop clone-fix / duplicate re-fires
        lastAdvance = now
        engine.advance()
        publishLogoBrightness(engine.logoIncomingMean()) // sync the logo flip with the morph
      },
      onTimer: (progress) => hero.style.setProperty('--home-timer', (1 - progress).toFixed(3)),
    })

    // Pause button toggles autoplay (and the timer ring).
    const pauseBtn = hero.querySelector('[data-home-pause]')
    if (pauseBtn) pauseBtn.addEventListener('click', () => {
      userPaused = !userPaused
      hero.classList.toggle('is-paused', userPaused)
      pauseBtn.setAttribute('aria-pressed', String(userPaused))
      syncAutoplay()
    })

    // Tap/click the hero to advance (unless locked by scroll). A mouse click
    // advances immediately — desktop scrolls the timeline with the wheel, never
    // a drag. Touch/pen, though, fires pointerdown at the START of a scroll
    // gesture too, so there we wait for pointerup and only advance on a "true
    // tap" (finger barely moved, lifted quickly). A swipe moves too far / is
    // cancelled, so it drives the scroll-timeline without changing a slide.
    const advanceZone = hero.querySelector('[data-home-advance]') || hero
    const advanceBlocked = (target) => locked || target.closest('a, button, [data-home-pause]')
    let tap = null
    advanceZone.addEventListener('pointerdown', (e) => {
      tap = null
      if (advanceBlocked(e.target)) return
      if (e.pointerType === 'mouse') { swiper.slideNext(); return }
      tap = { x: e.clientX, y: e.clientY, t: performance.now() }
    })
    advanceZone.addEventListener('pointerup', (e) => {
      const start = tap; tap = null
      if (!start || advanceBlocked(e.target)) return
      const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y)
      if (moved <= TAP_MOVE_MAX && performance.now() - start.t <= TAP_TIME_MAX) swiper.slideNext()
    })
    advanceZone.addEventListener('pointercancel', () => { tap = null })

    start()
  }).catch(() => { canvas.remove(); document.body.classList.remove('home-hero-active') }) // fallback stays
}

/** Reduced-motion / reduced-data fallback: a plain, autoplay-free slideshow in
 *  normal document flow — no WebGL, no scroll-timeline. Horizontal swipe (touch)
 *  and click/tap advance; near-vertical drags fall through to native page scroll
 *  (Swiper's touchAngle). The Autoplay module is deliberately NOT used here, so
 *  there is genuinely no autoplay. */
function initReducedMotionSlideshow(hero) {
  const el = hero.querySelector('.swiper')
  if (!el) return null
  const swiper = new Swiper(el, {
    loop: true,
    allowTouchMove: true, // horizontal swipe = slide change; vertical drag = native page scroll
    speed: SWIPER_SPEED_REDUCED, // this path only ever runs under .reduce-motion — see SWIPER_SPEED_REDUCED
    loopPreventsSliding: false,
  })
  // Click / tap anywhere on the hero advances. The .home-hero__advance overlay is
  // pointer-events:none under .reduce-motion (see home.sass), so clicks fall
  // through to the slides and bubble here; a real swipe suppresses the click.
  hero.addEventListener('click', (e) => {
    if (e.target.closest('a, button')) return
    swiper.slideNext()
  })
  return swiper
}

/**
 * Reduced-motion gallery: the SAME layered WebGL composition as the fancy hero
 * (centered front square over the full-bleed back, morph on advance) but with the
 * keyed colour bands turned OFF (engine.keyAmount = 0) — "the fancy one without the
 * shader". It stays in NORMAL document flow: no fixed takeover, no page-scroll
 * hijack, no scroll-timeline — you scroll past the one-viewport hero to the
 * calendar below. Autoplay is OFF by default (started PAUSED) so nothing moves
 * until the visitor taps the hero or presses play — respecting reduce-motion —
 * but the timer ring + pause button are shown so the slideshow can be opted into.
 * Degrades to the flat static slideshow if WebGL is unavailable.
 */
function initReducedMotionGallery(hero) {
  const sources = [...hero.querySelectorAll('[data-home-src]')].map((n) => n.dataset.homeSrc).filter(Boolean)
  if (!sources.length) return

  const canvas = document.createElement('canvas')
  canvas.className = 'home-hero__canvas'
  canvas.setAttribute('aria-hidden', 'true')
  hero.appendChild(canvas)

  let engine
  try { engine = new HomeGalleryEngine(canvas) }
  catch { canvas.remove(); initReducedMotionSlideshow(hero); return } // no WebGL → flat fallback
  engine.maxDpr = LIVE_MAX_DPR
  engine.maxSquare = Math.min(1280, engine.maxSquare)
  engine.keyAmount = 0 // "without the shader": no keyed colour bands, just the photos
  hero.classList.add('is-quiet-live') // CSS: re-show the pause button (hidden in flat reduce-motion)

  let running = false
  let rafId = 0
  let t0 = 0
  let elapsed = 0
  let lastDraw = 0
  let revealed = false
  let hidden = false
  let swiper = null
  let userPaused = true // start PAUSED — no autoplay by default
  let lastAdvance = 0

  const revealIfReady = () => {
    if (revealed || canvas.width <= 0 || canvas.height <= 0) return
    revealed = true
    hero.classList.add('is-home-live') // canvas ready → CSS crossfades the fallback out
  }
  const publishLogoBrightness = (mean) => {
    if (mean == null) return
    const isDark = mean < LOGO_DARK_CUT
    document.body.classList.toggle('logo-on-dark', isDark)
    window.dispatchEvent(new CustomEvent('home-hero:brightness', { detail: { mean, isDark } }))
  }

  // The loop runs ONLY while a morph is in flight: it draws the transition, then
  // parks on the settled frame. Nothing animates at rest (keying is off), so this
  // stays idle — respectful of reduce-motion and battery. advance()/visibility wake it.
  const tick = (now) => {
    if (engine._lost) { onLost(); return } // GPU context lost → flat fallback
    if (hidden) { stop(); return }
    rafId = requestAnimationFrame(tick)
    if (now - lastDraw < FRAME_MS) return
    lastDraw = now
    engine.render(elapsed + (now - t0) / 1000)
    revealIfReady()
    if (!engine.transition.active) stop() // settled → park until the next advance
  }
  const start = () => { if (running) return; running = true; t0 = performance.now(); rafId = requestAnimationFrame(tick) }
  const stop = () => { if (!running) return; running = false; cancelAnimationFrame(rafId); elapsed += (performance.now() - t0) / 1000 }
  const wake = () => { if (!hidden && !engine._lost) start() }

  const onLost = () => {
    stop()
    document.body.classList.remove('logo-on-dark')
    hero.classList.remove('is-home-live') // un-hide the fallback images
    canvas.remove()
    // If the Swiper controller is already up it keeps driving the now-visible
    // fallback as a plain slideshow (pause/tap still work) — only bootstrap a fresh
    // one if we lost the context before it existed.
    if (!swiper) { hero.classList.remove('is-quiet-live'); initReducedMotionSlideshow(hero) }
  }

  document.addEventListener('visibilitychange', () => {
    hidden = document.hidden
    if (hidden) stop(); else if (engine.transition.active) wake()
  })

  engine.setImages(sources).then(() => {
    lastAdvance = performance.now() // suppress any realIndexChange emitted during init
    publishLogoBrightness(engine.logoBackdropMean()) // seed the logo colour for the first slide

    swiper = buildSwiper(hero, {
      speed: SWIPER_SPEED_REDUCED, // .reduce-motion kills CSS transitions → Swiper must not wait on one
      onAdvance: () => {
        const now = performance.now()
        if (now - lastAdvance < MIN_ADVANCE_MS) return // ignore loop clone-fix / duplicate re-fires
        lastAdvance = now
        engine.advance()
        publishLogoBrightness(engine.logoIncomingMean())
        wake() // resume the loop to draw the morph
      },
      onTimer: (progress) => hero.style.setProperty('--home-timer', (1 - progress).toFixed(3)),
    })
    // Start PAUSED: Swiper's Autoplay module auto-starts on init, so stop it now.
    if (swiper && swiper.autoplay) swiper.autoplay.stop()
    hero.classList.add('is-paused')

    // Pause button toggles autoplay (and the timer ring). Starts showing "play".
    const pauseBtn = hero.querySelector('[data-home-pause]')
    if (pauseBtn) {
      pauseBtn.setAttribute('aria-pressed', 'true')
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused
        hero.classList.toggle('is-paused', userPaused)
        pauseBtn.setAttribute('aria-pressed', String(userPaused))
        if (swiper && swiper.autoplay) userPaused ? swiper.autoplay.stop() : swiper.autoplay.start()
      })
    }

    // Tap / click the hero advances one slide manually (even while paused). The
    // .home-hero__advance overlay is pointer-events:none under .reduce-motion, so
    // clicks bubble here; a real vertical scroll drag suppresses the click, leaving
    // native page scroll free to reach the calendar below.
    hero.addEventListener('click', (e) => {
      if (e.target.closest('a, button, [data-home-pause]')) return
      swiper ? swiper.slideNext() : (engine.advance(), wake())
    })

    start() // draw the initial settled composition (then the loop parks itself)
  }).catch(() => { // images failed → drop back to the flat fallback
    canvas.remove()
    hero.classList.remove('is-quiet-live')
    initReducedMotionSlideshow(hero)
  })

  // Reduce-motion: jump, don't animate the page scroll. 'instant', NOT 'auto' —
  // the reset sets `html { scroll-behavior: smooth }` site-wide and 'auto' defers
  // to that, so it would have animated exactly the visitor asked it not to.
  wireScrollDown(hero, () => scrollDocumentToReveal('instant'))
}

/** Swiper as a headless-ish controller: index + loop + autoplay + timer. */
function buildSwiper(hero, { onAdvance, onTimer, speed = SWIPER_SPEED }) {
  const el = hero.querySelector('.swiper')
  if (!el) return null
  return new Swiper(el, {
    modules: [Autoplay],
    loop: true,
    allowTouchMove: false, // wheel/touch is reserved for the scroll-timeline
    speed,
    // With speed 0 (reduce-motion) `animating` is never set, so this is only a
    // guard: never let a stuck `animating` flag deadlock every future slideNext().
    loopPreventsSliding: speed > 0,
    autoplay: { delay: AUTOPLAY_MS, disableOnInteraction: false },
    on: {
      // realIndexChange (not slideChange): with loop, Swiper's clone-fix re-emits
      // slideChange ~one transition later, which double-advanced the engine
      // ("slides but also reveals from back"). realIndexChange fires only on
      // genuine logical-slide changes.
      realIndexChange: () => onAdvance(),
      autoplayTimeLeft: (_s, _time, progress) => onTimer(progress),
    },
  })
}

/**
 * The "↓" button. It used to trail the cursor's X (JS-animated `left`); the client
 * asked for it to sit fixed in the centre (now pure CSS) and to scroll down to the
 * calendar when clicked. `onClick` does the scrolling — it differs per path: the
 * fancy hero plays its virtual timeline, the fallbacks scroll the document.
 */
function wireScrollDown(hero, onClick) {
  const btn = hero.querySelector('[data-home-scrolldown]')
  if (!btn || !onClick) return
  btn.addEventListener('click', (e) => { e.preventDefault(); onClick() })
}

/** Fallback paths (normal document flow): scroll the calendar into view. Pass an
 *  EXPLICIT behavior — 'auto' inherits `html { scroll-behavior: smooth }` from the
 *  reset, which is the wrong answer for the reduce-motion path. */
function scrollDocumentToReveal(behavior) {
  const reveal = document.querySelector('.home-hero__reveal')
  if (reveal) reveal.scrollIntoView({ behavior, block: 'start' })
}

if (document.body) {
  const hero = document.querySelector('[data-home-hero]')
  if (hero) {
    // The hero is full-screen at the top of the page, so it's already in view;
    // mirror the view-entry IO gate for consistency / safety.
    const io = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) { obs.disconnect(); init(hero); break }
      }
    }, { rootMargin: '200px' })
    io.observe(hero)
  }
}
